import {Component, Input, ViewChild} from '@angular/core';
import {PageEvent} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {TripProfitability} from '../../model/trip-profitability';
import {MatSort, Sort} from '@angular/material/sort';
import {FinanceReportsService} from '../../service/finance-reports.service';
import {ChartOptions} from 'chart.js';
import {ChartOptionsService} from 'src/app/shared/components/chart/chart-options.service';
import {ChartDataSet} from 'src/app/shared/components/chart/chart.component';
import {TripProfitabilityResponse} from '../../model/trip-profitability-response';
import {FilterService} from 'src/app/shared/service/filter/filter.service';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {FinanceReportFilterResult} from 'src/app/shared/model/finance-report-filter-result';
import moment from 'moment';
import {ExportGridService} from '../../../shared/service/files/export-grid.service';
import {DatePipe} from '@angular/common';

@Component({
    selector: 'app-trip-profitability',
    templateUrl: './trip-profitability.component.html',
    standalone: false
})
@AutoUnsubscribe()
export class TripProfitabilityComponent {
  @Input() filter!: FinanceReportFilterResult;

  pagination: Partial<PageEvent & {totalItemCount?: number}> = {};

  @ViewChild(MatSort, {static: false}) public sort!: MatSort;
  @ViewChild(MatSort)
  public set matSort(ms: MatSort) {
    this.sort = ms;
    this.dataSource.sort = this.sort;
  }
  public tripProfitabilityReportFilter: TripProfitability = {} as TripProfitability;
  public dataSource: MatTableDataSource<TripProfitability> = new MatTableDataSource<TripProfitability>([]);
  displayedColumns = [
    'tripReference',
    'dateCreated',
    'advisorName',
    'customerPaid',
    'vendorPaid',
    'commission',
    'agentSplit',
    'marginAmount',
    'marginPercentage',
    'adxBookingNumber'
  ];

  tripProfitabilityPieChartOptions: ChartOptions;
  tripProfitabilityPieChartData!: ChartDataSet;
  tripProfitabilityReportData: TripProfitability[] = [];

  private pieChartDataInitialized = false;
  private totalMarginRatios = { below25: 0, above25: 0 };

  constructor(
    private financeReportsService: FinanceReportsService,
    private exportGridService: ExportGridService,
    private datePipe: DatePipe,
    private chartOptions: ChartOptionsService,
    private filters: FilterService
    ) {
      this.tripProfitabilityPieChartOptions = this.chartOptions.getChartOptions('pie', {
        responsive: true,
        plugins: {
          datalabels:{
            display: false
          },
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Number Of Trips'
          },
        },
        elements: {
          arc: {
            borderWidth: 1
          },
        },
        scales: {
          x: {
            display: false
          },
          y: {
            display: false
          },
        },
      });
    }

  ngOnInit(): void {
    this.tripProfitabilityReportFilter.pageSize = 25;
    this.tripProfitabilityReportFilter.orderKey = 'Desc';
    this.tripProfitabilityReportFilter.propertyName = 'dateCreated';
    this.filters.getFinanceReportFilter().subscribe((filter: FinanceReportFilterResult) => {
      this.tripProfitabilityReportFilter.agentName = typeof filter.advisorName === 'string'
        ? filter.advisorName
        : (filter.advisorName as any)?.value || (filter.advisorName as any)?.AgentName || (filter.advisorName as any)?.agentName || '';

      this.tripProfitabilityReportFilter.tripReference = typeof filter.tripReference === 'string'
        ? filter.tripReference
        : (filter.tripReference as any)?.value || (filter.tripReference as any)?.ExternalReference || (filter.tripReference as any)?.externalReference || '';

      this.tripProfitabilityReportFilter.bookingStartFrom = filter.bookingStartFrom ? moment(filter.bookingStartFrom).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : "";
      this.tripProfitabilityReportFilter.bookingStartTo = filter.bookingStartTo ? moment(filter.bookingStartTo).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : "";

      this.tripProfitabilityReportFilter.bookingEndFrom = filter.bookingEndFrom ? moment(filter.bookingEndFrom).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : "";
      this.tripProfitabilityReportFilter.bookingEndTo = filter.bookingEndTo ? moment(filter.bookingEndTo).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : "";

      this.tripProfitabilityReportFilter.dateCreatedFrom = filter.dateCreatedFrom ? moment(filter.dateCreatedFrom).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : "";
      this.tripProfitabilityReportFilter.dateCreatedTo = filter.dateCreatedTo ? moment(filter.dateCreatedTo).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : "";

      this.tripProfitabilityReportFilter.pageNumber = 0;

      this.pagination = {
        pageSize: this.pagination.pageSize || 25,
        pageIndex: 0,
        totalItemCount: 0,
      };

      this.pieChartDataInitialized = false;
      this.getTripProfitabilityReport();
    });
  }

  public ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  getTripProfitabilityReport() {
    this.financeReportsService.getTripProfitabilityReport(this.tripProfitabilityReportFilter)
      .subscribe((data: TripProfitabilityResponse) => {
        if (data) {
          this.pagination = {
            ... this.pagination,
            pageIndex: data.pagination.pageNumber - 1,
            totalItemCount: data.pagination.totalItemCount,
          };

          this.tripProfitabilityReportData = data.items;
          this.tripProfitabilityReportFilter.pageNumber = data.pagination.pageNumber;
          this.dataSource = new MatTableDataSource(this.tripProfitabilityReportData);
          this.dataSource.sort = this.sort;

          if (!this.pieChartDataInitialized) {
            this.loadFullDatasetForChart();
          }
        }
      });
  }

  loadFullDatasetForChart() {
    const fullDataFilter = {
      ...this.tripProfitabilityReportFilter,
      pageNumber: 0,
      pageSize: 1000
    };

    this.financeReportsService.getTripProfitabilityReport(fullDataFilter)
      .subscribe((fullData: TripProfitabilityResponse) => {
        if (fullData && fullData.items) {
          this.totalMarginRatios.below25 = fullData.items.filter(i => i.marginPercentage <= 25).length;
          this.totalMarginRatios.above25 = fullData.items.filter(i => i.marginPercentage > 25).length;

          this.updatePieChartData();

          this.pieChartDataInitialized = true;
        }
      });
  }

  updatePieChartData() {
    this.tripProfitabilityPieChartData = {
      labels: [
        'Below 25% Margin Percentage',
        'Above 25% Margin Percentage'
      ],
      datasets: [{
        label: 'Trip Profitability Report',
        data: [this.totalMarginRatios.below25, this.totalMarginRatios.above25],
        backgroundColor: [
          '#EDBF6B',
          '#6BC4ED'
        ],
        hoverOffset: 0,
        spacing: 0
      }]
    };
  }

  pageChanged(event: PageEvent): void  {
    this.tripProfitabilityReportFilter.pageNumber = event.pageIndex + 1;
    this.getTripProfitabilityReport();
  }

  handleSortChange(sort: Sort): void {
    if (!sort.active || sort.direction === '') {
      return;
    }
    this.tripProfitabilityReportFilter.propertyName = this.getMappedBookingTblPropertyName(sort.active);
    this.tripProfitabilityReportFilter.orderKey = sort.direction.toUpperCase();
    this.getTripProfitabilityReport();
  }

  downloadGridToFile(): void {
    const maxRowsPerFile = 20000;

    const exportFilter = {
      ...this.tripProfitabilityReportFilter,
      pageSize: maxRowsPerFile,
      pageNumber: 1
    };

    this.exportGridService.getByDataSource(
      'finance-reports/trip-profitability',
      'trip-profitability',
      exportFilter,
      (data) => {
        const record = data as TripProfitability;
        return {
          'Trip Reference': record.tripReference,
          'Date Created': this.datePipe.transform(record.dateCreated, 'M/d/yyyy'),
          'Advisor Name': record.advisorName,
          'Customer Paid': record.customerPaid,
          'Vendor Paid': record.vendorPaid,
          'Commission': record.commission,
          'Agent Split': record.agentSplit,
          'Margin Amount': record.marginAmount,
          'Margin Percentage': record.marginPercentage,
          'ADX Booking Number': record.adxBookingNumber
        };
      }
    );
  }

  getMappedBookingTblPropertyName(propertyName: string): string {
    switch (propertyName) {
      case 'tripReference':
        return propertyName;
      case 'dateCreated':
        return propertyName;
      case 'advisorName':
        return 'agent.agentName';
      case 'customerPaid':
        return 'clientTotal';
      case 'vendorPaid':
        return 'bookingTotal';
      case 'commission':
        return 'commissionTotal';
      case 'agentSplit':
        return 'agent.splitRate';
      case 'adxBookingNumber':
        return 'externalBookingNumber';
      default:
        return '';
    }
  }
}
